<template>
    <kt-modal 
        :visible="true" 
        :title="isNew ? 'New Request' : 'Edit Request'"
        :size="'modal-lg'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <div v-if="isLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p class="mt-2">Loading...</p>
            </div>
            <template v-else>
                <!-- Deleted Record Toolbar -->
                <div v-if="record.Id && record.IsDeleted" class="alert alert-danger d-flex align-items-center justify-content-between mb-3 py-2">
                    <span><strong>This request has been deleted.</strong> You can restore it to make it active again.</span>
                    <button 
                        @click="restoreRequest" 
                        class="btn btn-sm btn-danger"
                        type="button"
                        :disabled="isProcessing"
                    >
                        <RotateCcw :size="14" class="me-1" />
                        Restore Request
                    </button>
                </div>
                
                <!-- Action Toolbar for existing records -->
                <div v-if="record.Id && !record.IsDeleted" class="d-flex gap-2 mb-3 pb-3 border-bottom">
                    <button 
                        @click="approveRequest" 
                        :class="['btn', 'btn-sm', record.Status === 2 ? 'btn-success' : 'btn-outline-success']"
                        :disabled="isProcessing"
                    >
                        <CheckCircle :size="14" class="me-1" />
                        Approve
                    </button>
                    <button 
                        @click="declineRequest" 
                        :class="['btn', 'btn-sm', record.Status === 4 ? 'btn-danger' : 'btn-outline-danger']"
                        :disabled="isProcessing"
                    >
                        <XCircle :size="14" class="me-1" />
                        Decline
                    </button>
                    <button 
                        @click="holdRequest" 
                        :class="['btn', 'btn-sm', record.Status === 1 ? 'btn-warning' : 'btn-outline-warning']"
                        :disabled="isProcessing"
                    >
                        <PauseCircle :size="14" class="me-1" />
                        On Hold
                    </button>
                    <div v-if="!record.RequestId && record.Id" class="-mt-2">
                    <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="startClock"
                        title="Start Clock"
                        type="button"
                    >
                        <PlayCircle :size="16" class="me-1" />
                        Start Clock
                    </button>
                    <small class="text-muted ms-2">Track Pre-Approved Time</small>
                </div>
                    <button 
                        @click="deleteRequest" 
                        class="btn btn-sm btn-outline-danger ms-auto"
                        :disabled="isProcessing"
                    >
                        <Trash2 :size="14" class="me-1" />
                        Delete
                    </button>
                </div>
                
                <request-form v-model="record"></request-form>
            </template>
        </template>

        <template #footer>
            <div class="kt-modal-actions">
                <button @click="onDismiss()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button @click="saveRecord()" class="btn btn-primary" :disabled="isSaving || isLoading || isProcessing">
                    <Save :size="16" class="me-1" />
                    {{ isSaving ? 'Saving...' : 'Save Request' }}
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import Helper, { DateFormat } from "@/services/Helper";
import { WorkOrderModel } from "@/models/WorkOrderModel";
import KtModal from "@/components/UI/KtModal.vue";
import RequestForm from "@/components/Requests/RequestForm.vue";
import { X, Save, CheckCircle, XCircle, PauseCircle, Trash2, RotateCcw } from 'lucide-vue-next';
import { JobModel } from '@/models/RequestModel';
import { TimeModelFilter } from '@/models/TaskModel';

const route = useRoute();
const router = useRouter();

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    }
});

const emit = defineEmits(['close']);

const record = ref<WorkOrderModel>(new WorkOrderModel());
const isSaving = ref(false);
const isLoading = ref(false);
const isProcessing = ref(false);

// Check if we're in route-based mode or prop-based mode
// Only consider it route mode if we're actually on the /request/edit route
const isRouteMode = computed(() => route.path.startsWith('/request/edit'));

const isNew = computed(() => {
    if (isRouteMode.value) {
        return route.params.id === 'new' || !route.params.id;
    }
    return !props.id;
});

const initializeRecord = () => {
    record.value = new WorkOrderModel();
    record.value.Title = 'Request'; // Type field - used for filtering
    record.value.RequestTitle = ''; // Actual request title entered by user
    record.value.Priority = 'Low';
    record.value.Status = 0; // Draft
    record.value.NotifyUpdates = false;
    record.value.DateCreated = Helper.dateToInput(new Date(), DateFormat.Date);
    record.value.DateDue = Helper.dateToInput(new Date(), DateFormat.Date); // Default Needed By to today
};

const loadRecord = async (id: string | number) => {
    isLoading.value = true;
    try {
        const loaded = await Api.loadWorkOrder(id);
        record.value = loaded;
    } catch (error) {
        console.error("Error loading request:", error);
        Alert.notify({ text: "Failed to load request", type: "error" });
        onDismiss();
    } finally {
        isLoading.value = false;
    }
};

const onDismiss = () => {
    if (isRouteMode.value) {
        router.push('/request/list');
    } else {
        emit('close');
    }
};

const saveRecord = async () => {
    try {
        // Validate
        if (!record.value.RequestTitle?.trim()) {
            Alert.notify({ text: "Please enter a title", type: "error" });
            return;
        }

        // Validate Deadline priority requires DateDue
        if (/* record.value.Priority === 'Deadline' && */ !record.value.DateDue) {
            Alert.notify({ text: "Please enter a Needed By date.", type: "error" });
            return;
        }

        isSaving.value = true;

        // Ensure it's marked as a Request type (Title is the type field)
        record.value.Title = 'Request';

        // Save
        const saved = await Api.saveWorkOrder(record.value);
        
        Toast.success(isNew.value ? "Request created successfully" : "Request updated successfully");
        
        // Refresh datatables
        Emitter.emit('datatable-refresh', 'requests-list');
        Emitter.emit('datatable-refresh', 'workorder-list');
        
        onDismiss();
    } catch (error) {
        console.error("Error saving request:", error);
        Alert.notify({ text: "Failed to save request", type: "error" });
    } finally {
        isSaving.value = false;
    }
};

// ===== ACTION TOOLBAR FUNCTIONS =====

const startClock = () => {
    if (!record.value?.Id) return;
    Emitter.emit('new:clock', { requestId: record.value.Id });
};

const mapPriorityToNumber = (priority?: string | null): number => {
    switch (priority?.toLowerCase()) {
        case 'critical': return 1;
        case 'deadline': return 1;
        case 'high': return 2;
        case 'medium': return 3;
        case 'low': return 4;
        case 'cancelled': return 5;
        case 'schedule': return 5;
        default: return 5; // Default to Schedule/None (lowest priority)
    }
};

const approveRequest = () => {
    // Validate Owner field - show popup if missing
    if (!record.value.Owner) {
        Alert.notify({ text: 'Please assign a Technician (Owner) before approving this request.', type: 'warning' });
        return;
    }

    // Check if a Job already exists for this Request
    // Use explicit null/undefined check since 0 is falsy but could be a valid (though unlikely) ID
    const existingJobId = record.value.RequestId ?? record.value.Request?.Id ?? null;
    const hasExistingJob = existingJobId !== null && existingJobId !== undefined && existingJobId > 0;
    
    const confirmMessage = hasExistingJob
        ? 'This request already has a linked Job. Do you want to approve and open the existing Job?'
        : 'Are you sure you want to approve this request? This will create a new Job/Work Order from this request.';
    
    Alert.confirm(
        confirmMessage,
        async () => {
            isProcessing.value = true;
            try {
                let jobId = existingJobId;
                
                // Only create a new Job if one doesn't already exist
                if (!hasExistingJob) {
                    // Create a new Job from the Request data
                    const job = new JobModel();
                    job.Customer = record.value.ContactId;
                    job.RequestDetailedDescription = record.value.RequestTitle;
                    job.TaskDetail = record.value.Description;
                    job.Priority = mapPriorityToNumber(record.value.Priority);
                    job.Location = record.value.Location;
                    job.DueDate = record.value.DateDue;
                    job.RequestStatus = 1; // Active/Open
                    job.CreateDate = Helper.dateToInput(new Date(), DateFormat.Date);

                    // Inherit Department from Request to Job
                    job.Department = record.value.DepartmentCategory || null;

                    // Inherit Owner (Technician) from Request to Job
                    job.Technician = record.value.Owner || null;

                    // Save the Job
                    const savedJob = await Api.saveRequest(job);
                    
                    if (!savedJob?.Id) {
                        Alert.notify({ text: 'Failed to create Job', type: 'error' });
                        return;
                    }
                    
                    jobId = savedJob.Id;
                    
                    // Update the WorkOrder with the new Job ID
                    record.value.RequestId = jobId;
                }
                
                // Set status to Approved
                record.value.Status = 2; // Approved
                await Api.saveWorkOrder(record.value);
                
                Toast.success(hasExistingJob ? 'Request approved' : 'Request approved and Job created successfully');
                
                // Refresh datatables
                Emitter.emit('datatable-refresh', 'requests-list');
                Emitter.emit('datatable-refresh', 'workorder-list');
                
                // Close this modal
                onDismiss();
                
                // Check if the Job has any Tasks assigned
                const taskFilter = new TimeModelFilter();
                taskFilter.RequestId = [jobId as number];
                taskFilter.Size = 1; // We only need to know if at least one exists
                
                const tasksResponse = await Api.loadTimes(taskFilter, true);
                const hasTasks = tasksResponse?.Data && tasksResponse.Data.length > 0;
                
                // Always open the Job edit modal
                Emitter.emit('edit:job', jobId);
                
                if (!hasTasks) {
                    // Job has no tasks, also open the Assignment form with Job pre-selected
                    Emitter.emit('open:taskForm', {
                        id: null, // New assignment
                        requestId: jobId,
                        clientId: record.value.ContactId,
                        workAssignment: record.value.Description || record.value.RequestTitle || ''
                    });
                }
            } catch (error) {
                console.error('Error approving request:', error);
                Alert.notify({ text: 'Failed to approve request', type: 'error' });
            } finally {
                isProcessing.value = false;
            }
        },
        () => {} // Cancel callback
    );
};

const declineRequest = () => {
    Alert.confirm(
        'Are you sure you want to decline this request?',
        async () => {
            isProcessing.value = true;
            try {
                record.value.Status = 4; // Declined/Cancelled
                await Api.saveWorkOrder(record.value);
                
                Toast.success('Request declined');
                Emitter.emit('datatable-refresh', 'requests-list');
                onDismiss();
            } catch (error) {
                console.error('Error declining request:', error);
                Alert.notify({ text: 'Failed to decline request', type: 'error' });
            } finally {
                isProcessing.value = false;
            }
        },
        () => {} // Cancel callback
    );
};

const holdRequest = () => {
    Alert.confirm(
        'Are you sure you want to put this request on hold?',
        async () => {
            isProcessing.value = true;
            try {
                record.value.Status = 1; // On Hold/Pending
                await Api.saveWorkOrder(record.value);
                
                Toast.success('Request placed on hold');
                Emitter.emit('datatable-refresh', 'requests-list');
                onDismiss();
            } catch (error) {
                console.error('Error placing request on hold:', error);
                Alert.notify({ text: 'Failed to place request on hold', type: 'error' });
            } finally {
                isProcessing.value = false;
            }
        },
        () => {} // Cancel callback
    );
};

const deleteRequest = () => {
    Alert.confirm(
        'Are you sure you want to delete this request?',
        async () => {
            isProcessing.value = true;
            try {
                await Api.deleteWorkOrder(record.value.Id!);
                
                Toast.success('Request deleted');
                Emitter.emit('datatable-refresh', 'requests-list');
                onDismiss();
            } catch (error) {
                console.error('Error deleting request:', error);
                Alert.notify({ text: 'Failed to delete request', type: 'error' });
            } finally {
                isProcessing.value = false;
            }
        },
        () => {} // Cancel callback
    );
};

const restoreRequest = async () => {
    isProcessing.value = true;
    try {
        await Api.restoreWorkOrder(record.value.Id!);
        
        Toast.success('Request restored successfully');
        Emitter.emit('datatable-refresh', 'requests-list');
        
        // Reload the record to get updated state
        await loadRecord(record.value.Id!);
    } catch (error) {
        console.error('Error restoring request:', error);
        Alert.notify({ text: 'Failed to restore request', type: 'error' });
    } finally {
        isProcessing.value = false;
    }
};

onMounted(async () => {
    if (isNew.value) {
        initializeRecord();
    } else {
        // Load from prop id or route param
        const recordId = isRouteMode.value ? route.params.id as string : props.id;
        if (recordId) {
            await loadRecord(recordId);
        }
    }
});
</script>
